import React, { useState, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, GraduationCap, ClipboardList, Calculator, Route as RouteIcon,
  Trophy, Gift, Settings, User, Bell, Search, Menu, LogOut, X, CalendarClock,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { scholarships } from "@/data/scholarships";
import { daysUntil } from "@/lib/calc";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/beasiswa", label: "Beasiswa", icon: GraduationCap },
  { to: "/app/test", label: "Simulasi Test", icon: ClipboardList },
  { to: "/app/kalkulator", label: "Kalkulator Biaya", icon: Calculator },
  { to: "/app/roadmap", label: "Roadmap", icon: RouteIcon },
  { to: "/app/benefit", label: "Benefit Explorer", icon: Gift },
  { to: "/app/kisah-sukses", label: "Kisah Sukses", icon: Trophy },
];

const mobileNav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/beasiswa", label: "Beasiswa", icon: GraduationCap },
  { to: "/app/roadmap", label: "Roadmap", icon: RouteIcon },
  { to: "/app/test", label: "Test", icon: ClipboardList },
  { to: "/app/profil", label: "Profil", icon: User },
];

function SidebarLink({ item, onClick }) {
  const { icon: Icon, to, label, end } = item;
  return (
    <NavLink
      to={to} end={end} onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive ? "bg-[#F3E8FF] text-[#4C1D95]" : "text-slate-600 hover:bg-[#FAF7FF] hover:text-[#5B21B6]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#7C3AED]" />}
          <Icon className="h-[18px] w-[18px]" />
          {label}
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5">
        <Logo />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {nav.map((item) => <SidebarLink key={item.to} item={item} onClick={onNavigate} />)}
      </nav>
      <div className="space-y-1 border-t border-purple-100 px-3 py-3">
        <SidebarLink item={{ to: "/app/pengaturan", label: "Settings", icon: Settings }} onClick={onNavigate} />
        <SidebarLink item={{ to: "/app/profil", label: "Profile", icon: User }} onClick={onNavigate} />
      </div>
    </div>
  );
}

export function AppShell({ children }) {
  const { user, displayName, logout, data } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const initials = (displayName || "S").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const deadlineItems = useMemo(() => {
    const savedIds = data?.savedScholarships || [];
    return scholarships
      .filter((s) => s.deadline)
      .map((s) => ({ s, d: daysUntil(s.deadline), saved: savedIds.includes(s.id) }))
      .filter((x) => x.d > 0)
      .sort((a, b) => (b.saved - a.saved) || (a.d - b.d));
  }, [data]);
  const urgentCount = deadlineItems.filter((x) => x.d <= 30).length;

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-purple-100 bg-white lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button className="absolute right-3 top-3 rounded-lg p-2 text-slate-400" onClick={() => setDrawer(false)} data-testid="drawer-close">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setDrawer(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 border-b border-purple-100 bg-white/85 backdrop-blur-md">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            <button className="rounded-lg p-2 text-slate-500 hover:bg-purple-50 lg:hidden" onClick={() => setDrawer(true)} data-testid="open-drawer">
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden flex-1 items-center gap-2 rounded-xl border border-purple-100 bg-[#FBFAFF] px-3 py-2 sm:flex sm:max-w-md">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                data-testid="topbar-search"
                placeholder="Cari beasiswa, universitas, atau negara..."
                className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
              />
            </div>
            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative rounded-xl border border-purple-100 bg-white p-2 text-slate-500 hover:bg-purple-50" data-testid="notif-btn">
                    <Bell className="h-[18px] w-[18px]" />
                    {urgentCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#7C3AED] px-1 text-[10px] font-bold text-white">{urgentCount}</span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 p-0">
                  <div className="flex items-center justify-between border-b border-purple-100 px-3 py-2.5">
                    <p className="text-sm font-bold text-slate-800">Pengingat Deadline</p>
                    <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-[#6D28D9]">{deadlineItems.length} aktif</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto py-1" data-testid="deadline-list">
                    {deadlineItems.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-slate-400">Tidak ada deadline mendatang.</p>
                    ) : deadlineItems.map(({ s, d, saved }) => (
                      <button key={s.id} onClick={() => navigate(`/app/beasiswa/${s.id}`)} data-testid={`deadline-${s.id}`}
                        className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-purple-50/60">
                        <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${d <= 30 ? "bg-red-50 text-red-500" : "bg-purple-50 text-[#7C3AED]"}`}>
                          <CalendarClock className="h-4 w-4" />
                        </span>
                        <span className="flex-1">
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">{s.name}{saved && <span className="rounded bg-emerald-50 px-1 text-[9px] font-bold text-emerald-600">TERSIMPAN</span>}</span>
                          <span className="block text-xs text-slate-400">{s.deadline} · {s.country}</span>
                        </span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${d <= 30 ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{d} hari</span>
                      </button>
                    ))}
                  </div>
                  <DropdownMenuSeparator />
                  <button onClick={() => navigate("/app/beasiswa")} className="w-full px-3 py-2.5 text-center text-sm font-semibold text-[#7C3AED] hover:bg-purple-50">Lihat semua beasiswa →</button>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-xl border border-purple-100 bg-white py-1 pl-1 pr-2.5 hover:bg-purple-50" data-testid="user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] text-xs font-bold text-white">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden text-sm font-semibold text-slate-700 sm:block" data-testid="topbar-username">Halo, {displayName || "👋"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold text-slate-800">{displayName}</p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/app/profil")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> Profil</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/app/pengaturan")}><Settings className="mr-2 h-4 w-4" /> Pengaturan</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" /> Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1280px] px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-10">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-purple-100 bg-white/95 backdrop-blur-md lg:hidden">
        <div className="grid grid-cols-5">
          {mobileNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to} to={to} end={end}
              data-testid={`mobilenav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${isActive ? "text-[#7C3AED]" : "text-slate-400"}`
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
